import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, ReloadOutlined, WarningOutlined } from "@ant-design/icons";
import { Col, Flex, Form, List, Modal, Popconfirm, Row, Select, Tabs, Tooltip, Typography } from "antd";
import FixedButton from "components/FixedButton";
import ContentWithHeader from "components/Layout/AppLayout/Content/ContentWithHeader";
import useGetListasPrecios from "hooks/useGetListasPrecios";
import useGetSecciones from "hooks/useGetSecciones";
import useStyles from "hooks/useStyles";
import { useEffect, useState } from "react";
import { filterOption } from "utils";
import Adicionales from "./components/Adicionales";
import ListaPrecios from "./components/ListaPrecios";
import Renovar from "./components/Renovar";
import Seguros from "./components/Seguros";

const AumentoPrecios = () => {
  const { algorithm } = useStyles();
  
  const colStyle = {
    maxHeight: "calc(100vh * 0.5)",
    overflowY: "auto",
    padding: 0,
  };

  const colPadding = {
    padding: '0 12px'
  }

  const styleSticky = {
    padding: '12px',
    position: "sticky",
    top: 0,
    zIndex: 10,
    backgroundColor: algorithm === "dark" ? "#1D1D1D" : "#F5F5F5",
    marginBottom: 8,
  };

  const { seccionesOptions } = useGetSecciones();
  const { listasPreciosOptions, fetchListasPrecios } = useGetListasPrecios();

  const [seccionId, setSeccionId] = useState(null);
  const [preciosSeleccionados, setPreciosSeleccionados] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [activeInnerTab, setActiveInnerTab] = useState("1");
  const [checked, setChecked] = useState([]);
  const [checkedAdicionales, setCheckedAdicionales] = useState([]);
  const [preciosSeguros, setPreciosSeguros] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState({});
  const [openRenovar, setOpenRenovar] = useState(false);
  const [storedValues, setStoredValues] = useState([]);

  const estoyEnListaPrecios = activeInnerTab === "1";
  const estoyEnAdicionales  = activeInnerTab === "2";
  const estoyEnSeguros  = activeInnerTab === "3";
  const title = (estoyEnSeguros ? "Seguros" : estoyEnAdicionales ? "Adicionales" : "Lista de Precios");

  const handleChangeSeccion = (value) => {
    setSeccionId(value);
    fetchListasPrecios(value);
    setPreciosSeleccionados([]);
    setActiveTab(null);
    setChecked([]);
    setCheckedAdicionales([]);
  };

  const handleChangeListasPrecios = (values) => {
    setPreciosSeleccionados(values);
    setChecked(prev => prev.filter(x => values.includes(x.id)));
    setCheckedAdicionales(prev => prev.filter(x => values.includes(x.id)));
  }

  const handleChangeTab = (key) => {
    setActiveTab(Number(key));
  };

  const handleChangeInnerTab = (key) => {
    setActiveInnerTab(key);
  };

  const handleRenovar = () => {
    if (!estoyEnSeguros) {
      const errores = [];
      let hayCambios = false;

      for (const precioId of preciosSeleccionados) {
        const precio = checked.find(x => x.id === precioId);
        const adicional = checkedAdicionales.find(x => x.id === precioId);
        const nombre = listasPreciosOptions.find(x => x.value === precioId)?.label ?? precioId;

        if (estoyEnListaPrecios) {
          const aplicaLP = precio?.tramos?.length > 0;

          // si no tiene tramos, no valida y tampoco marca cambios
          if (aplicaLP) {
            const faltantes = [];
            if (precio.articulos.length === 0) faltantes.push("ARTÍCULOS");
            if (precio.unidades.length === 0)  faltantes.push("UNIDADES");

            // 🔥 PRIORIDAD: detectar errores primero
            if (faltantes.length > 0) {
              errores.push(`${nombre}: faltan ${faltantes.join(", ")}`);
            } else {
              // si no faltan datos → entonces hubo cambios válidos
              hayCambios = true;
            }
          }
        }

        if (estoyEnAdicionales) {
          const aplicaAD = adicional?.grupos?.length > 0;

          if (aplicaAD) {
            const faltantes = [];
            if (adicional.articulos.length === 0) faltantes.push("ARTÍCULOS");
            if (adicional.unidades.length === 0)  faltantes.push("UNIDADES");

            // 🔥 errores prioridad
            if (faltantes.length > 0) {
              errores.push(`${nombre}: faltan ${faltantes.join(", ")}`);
            } else {
              hayCambios = true;
            }
          }
        }
      }

      // 🔥 SI HAY ERRORES → mostrar errores SIEMPRE primero
      if (errores.length > 0) {
        Modal.error({
          title: "No se puede renovar",
          width: 520,
          content: (
            <List
              size="small"
              header={<Typography.Title level={5}>Revisá las siguientes listas:</Typography.Title>}
              dataSource={errores}
              renderItem={(item) => <List.Item>{item}</List.Item>}
            />
          ),
          okText: "Entendido",
        });
        return;
      }

      // 🔥 SIN errores Y SIN cambios → mostrar mensaje
      if (!hayCambios) {
        Modal.info({
          title: `No hay cambios para renovar en ${title}`,
          content: "No modificaste ninguna lista de precios.",
          okText: "Entendido",
        });
        return;
      }
    } else {
      // Sin cambios → Mostrar mensaje y salir
      if (!hasUnsavedChanges[activeTab]) {
        Modal.info({
          title: `No hay cambios para renovar en ${title}`,
          content: "No modificaste nada en esta lista de precios.",
          okText: "Entendido",
        });
        return;
      }
    }

    // Filtrar solo precios válidos (con tramos Y artículos Y unidades)
    const preciosValidos = checked.filter(x =>
      x.tramos?.length > 0 &&
      x.articulos?.length > 0 &&
      x.unidades?.length > 0
    );

    // Filtrar solo adicionales válidos (con grupos Y artículos Y unidades)
    const adicionalesValidos = checkedAdicionales.filter(x =>
      x.grupos?.length > 0 &&
      x.articulos?.length > 0 &&
      x.unidades?.length > 0
    );

    let segurosValidos = [];

    if (estoyEnSeguros) {
      const item = preciosSeguros.find(x => x.id === activeTab);

      if (item) {
        const editados = item.seguros.filter(s => s.editado === true);

        if (editados.length > 0) {
          segurosValidos = [{
            id: activeTab,
            seguros: editados
          }];
        }
      }
    }

    setStoredValues({
      precio: preciosValidos,
      adicional: adicionalesValidos,
      seguro: segurosValidos,
    });

    setOpenRenovar(true);
  };

  const handleAfterRenovar = () => {
    if (estoyEnListaPrecios) {
      setChecked([]);
      setActiveInnerTab("2");
    }

    if (estoyEnAdicionales) {
      setCheckedAdicionales([]);
      setActiveInnerTab("3");
    }

    if (estoyEnSeguros) {
      setHasUnsavedChanges(prev => ({
        ...prev,
        [activeTab]: false,
      }));
    }
  };

  useEffect(() => {
    if (preciosSeleccionados.length === 0) return;

    // activar automáticamente el último precio agregado
    const ultimoPrecio = preciosSeleccionados[preciosSeleccionados.length - 1];
    setActiveTab(ultimoPrecio);
  }, [preciosSeleccionados]);

  return (
    <ContentWithHeader title="Aumento de precios">
      <Row gutter={24}>
        <Col xs={8}>
          <Form.Item label="Sección">
            <Select
              showSearch
              filterOption={filterOption}
              placeholder="Seleccionar sección"
              options={seccionesOptions}
              onChange={handleChangeSeccion}
            />
          </Form.Item>
        </Col>

        <Col xs={14}>
          <Form.Item label="Listas de precios">
            <Select
              mode="multiple"
              disabled={!seccionId}
              placeholder="Seleccionar listas de precios"
              maxTagCount="responsive"
              options={listasPreciosOptions}
              filterOption={filterOption}
              value={preciosSeleccionados}
              onChange={handleChangeListasPrecios}
            />
          </Form.Item>
        </Col>
      </Row>

      {preciosSeleccionados?.length > 0 && (
        <>
          <Tabs
            className="custom-tab"
            type="card"
            tabPosition="left"
            activeKey={activeTab}
            onChange={handleChangeTab}
            style={{ maxHeight: "calc(100vh * 0.6)" }}
            animated={{ tabPane: true }}
            tabBarExtraContent={{
              left: <Typography.Title level={5}>Listas de precios</Typography.Title>,
            }}
            items={preciosSeleccionados.map(precioId => {
              const precioSeleccionado = listasPreciosOptions?.find(s => s.value === precioId);

              return ({
                key: precioId,
                label: (
                  (() => {
                    const precio = checked.find(x => x.id === precioId);
                    const adicional = checkedAdicionales.find(x => x.id === precioId);

                    let isChecked = false;
                    let isIndeterminate = false;
                    let tooltipText = "";
                    let seguroWithWarning = false;

                    if (estoyEnListaPrecios) {
                      if (precio && precio?.tramos?.length !== 0) {
                        const completos =
                          precio.articulos.length > 0 &&
                          precio.unidades.length > 0;

                        const faltantes = [];
                        if (precio.articulos.length === 0) faltantes.push("ARTÍCULOS");
                        if (precio.unidades.length === 0)  faltantes.push("UNIDADES");

                        isChecked = completos;
                        isIndeterminate = !completos;
                        tooltipText = faltantes.length ? `Faltan: ${faltantes.join(", ")}` : "";
                      }
                    }

                    if (estoyEnAdicionales) {
                      if (adicional && adicional?.grupos?.length !== 0) {
                        const completos =
                          adicional.articulos.length > 0 &&
                          adicional.unidades.length > 0;

                        const faltantes = [];
                        if (adicional.articulos.length === 0) faltantes.push("ARTÍCULOS");
                        if (adicional.unidades.length === 0)  faltantes.push("UNIDADES");

                        isChecked = completos;
                        isIndeterminate = !completos;
                        tooltipText = faltantes.length ? `Faltan: ${faltantes.join(", ")}` : "";
                      }
                    }

                    if (estoyEnSeguros) {
                      seguroWithWarning = hasUnsavedChanges[precioId];
                      tooltipText = "Tenes cambios sin guardar";
                    }

                    return (
                      <Flex align="center" gap={8} style={{ minWidth: 240 }}>
                        {isIndeterminate ? (
                          <Tooltip title={tooltipText}>
                            <ExclamationCircleOutlined style={{ color: '#D32029', fontSize: 16 }} />
                          </Tooltip>
                        ) : isChecked ? (
                          <CheckCircleOutlined style={{ color: '#49AA19', fontSize: 16 }} />
                        ) : seguroWithWarning ? (
                          <Tooltip title={tooltipText}>
                            <WarningOutlined style={{ color: '#E2B205', fontSize: 16 }} />
                          </Tooltip>
                        ) : (
                          <Popconfirm
                            title="¿Desea quitar esta lista de precios?"
                            onConfirm={() => setPreciosSeleccionados(prev => prev.filter(id => id !== precioId))}
                          >
                            <CloseCircleOutlined style={{ color: '#4F4F4F', fontSize: 16 }} />
                          </Popconfirm>
                        )}
                        {precioSeleccionado?.label}
                      </Flex>
                    );
                  })()
                ),
                children: (
                  <Tabs
                    activeKey={activeInnerTab}
                    onChange={handleChangeInnerTab}
                    items={[
                      {
                        key: "1",
                        label: "Lista de precios",
                        children: (
                          <ListaPrecios
                            precioId={precioId}
                            unidadMedida={precioSeleccionado?.unidad_medida}
                            checked={checked}
                            setChecked={setChecked}
                            styles={{ colStyle, styleSticky, colPadding }}
                          />
                        )
                      },
                      {
                        key: "2",
                        label: "Retiro y entrega",
                        children: (
                          <Adicionales
                            precioId={precioId}
                            unidadMedida={precioSeleccionado?.unidad_medida}
                            checkedAdicionales={checkedAdicionales}
                            setCheckedAdicionales={setCheckedAdicionales}
                            styles={{ colStyle, styleSticky, colPadding }}
                          />
                        )
                      },
                      {
                        key: "3",
                        label: "Seguros",
                        children: (
                          <Seguros
                            precioId={precioId}
                            setHasUnsavedChanges={setHasUnsavedChanges}
                            preciosSeguros={preciosSeguros}
                            setPreciosSeguros={setPreciosSeguros}
                          />
                        )
                      }
                    ]}
                  />
                )
              })
            })}
          />
          <FixedButton
            type="primary"
            onClick={handleRenovar}
            icon={<ReloadOutlined style={{ color: '#ffff' }} />}
            bottom={50}
            right={20}
            title={`Renovar ${title}`}
          />
        </>
      )}
      {openRenovar &&
        <Renovar
          title={title}
          estoyEnListaPrecios={estoyEnListaPrecios}
          estoyEnAdicionales={estoyEnAdicionales}
          estoyEnSeguros={estoyEnSeguros}
          storedValues={storedValues}
          onAfterRenovar={handleAfterRenovar}
          closeModal={() => setOpenRenovar(false)}
        />
      }
    </ContentWithHeader>
  );
}

export default AumentoPrecios;